import { useEffect, useRef, useState } from "react";
import { FormikProps } from "formik";
import TransactionsType from "enums/TransactionsType";
import { User } from "hooks/useGetLoggedInUser";
import {
  FormikCreateExpense,
  getExpenseInitialValuesByType,
} from "utils/reimbursements/reimbursementExpenseFormat";

export enum ReimbursementSteps {
  ENTER_DETAILS = "ENTER_DETAILS",
  SUMMARY = "SUMMARY",
}

interface UseReimbursementLifecycleArgs {
  open: boolean;
  /** Editing loads the saved reimbursement, so opening must not reset it. */
  editing: boolean;
  /** False when the payee is chosen, so the form starts with nobody picked. */
  payViewer: boolean;
  /** The payee's custom field defaults, which a reopen must restore rather than clear. */
  customFieldDefaults?: FormikCreateExpense["customFields"];
  user: User | null;
  formik: FormikProps<FormikCreateExpense>;
  resetFormValues: (type: TransactionsType | null) => void;
  setExpenseType: (type: TransactionsType | null) => void;
  requestLeave: () => void;
}

/**
 * Open/reset lifecycle, default payee, and step navigation for the reimbursement
 * drawer. `payTo` is seeded into the reset BASELINE (via `resetForm` values) so
 * an untouched drawer isn't dirty and a plain Cancel doesn't wrongly trigger the
 * unsaved-changes prompt.
 */
export function useReimbursementLifecycle({
  open,
  editing,
  payViewer,
  customFieldDefaults,
  user,
  formik,
  resetFormValues,
  setExpenseType,
  requestLeave,
}: UseReimbursementLifecycleArgs) {
  const [currentStep, setCurrentStep] = useState<ReimbursementSteps>(
    ReimbursementSteps.ENTER_DETAILS,
  );

  // Reset only on the closed → open transition. Depending on `user` here would
  // re-run the reset (wiping in-progress input) whenever the logged-in user
  // object changes while the drawer is open; the late-arriving user is instead
  // seeded by the effect below.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened || editing) return;

    setExpenseType(null);
    setCurrentStep(ReimbursementSteps.ENTER_DETAILS);
    formik.resetForm({
      values: {
        ...getExpenseInitialValuesByType(null),
        ...(customFieldDefaults && { customFields: customFieldDefaults }),
        ...(payViewer &&
          user && {
            payTo: {
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
            },
          }),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  // A fallback: the reset above already puts the viewer in the baseline.
  useEffect(() => {
    if (open && !editing && payViewer && user && !formik.values.payTo?.id) {
      formik.setFieldValue("payTo", {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, formik.values.payTo?.id]);

  // For the drawer's `onAfterClose`, so the content holds still while it slides out.
  const resetState = () => {
    setExpenseType(null);
    resetFormValues(null);
    setCurrentStep(ReimbursementSteps.ENTER_DETAILS);
  };

  const handlePreviousStep = () => {
    if (currentStep === ReimbursementSteps.SUMMARY) {
      setCurrentStep(ReimbursementSteps.ENTER_DETAILS);
      return;
    }
    requestLeave();
  };

  return {
    currentStep,
    setCurrentStep,
    resetState,
    handlePreviousStep,
  };
}
